import { defineComponent as _defineComponent } from 'vue'
import { shiftApi } from '@/api';
import Button from '@/components/buttons/Button.vue';
import ButtonMenu from '@/components/buttons/ButtonMenu.vue';
import Dialog from '@/components/dialog/Dialog.vue';
import DialogFooter from '@/components/dialog/DialogFooter.vue';
import InputGroup from '@/components/form/InputGroup.vue';
import PageTabs from '@/components/layout/PageTabs.vue';
import LoadingDot from '@/components/loaders/LoadingDot.vue';
import LoadingOverlay from '@/components/loaders/LoadingOverlay.vue';
import i18n from '@/i18n';
import { dateTimeFormats } from '@/lang/dateTimeFormats';
import {
  ShiftActionType,
  useShiftActions,
} from '@/lib/actions/shiftActionsComposable';
import { ShiftActionControlEnum } from '@/lib/actions/ShiftActionsController';
import { useOpen } from '@/lib/composition/dialog';
import { PlainDate } from '@/lib/date-time/PlainDate';
import { fullName } from '@/lib/employee/employeeFunctions';
import { InlineDialogTheme } from '@/lib/enum/Colour';
import { Icon } from '@/lib/enum/Icon';
import { belongsToEmployee } from '@/lib/filters/employeeFilters';
import { Entity } from '@/lib/realtime/RealtimeEntity';
import {
  realtimeQuery,
  realtimeQueryInstantUpdate,
} from '@/lib/realtime/weak/realtimeFunctions';
import { WeakEntityRepositoryUpdateKind } from '@/lib/realtime/weak/WeakEntityRepository';
import { EmitCreateShiftData } from '@/lib/schedule-v2/scheduleV2Actions';
import {
  isNightShift,
  presentShiftTimes,
} from '@/lib/schedule-v2/scheduleV2Functions';
import {
  ConflictingCustomShiftMessage,
  getConflictingCustomShiftMessage,
} from '@/lib/schedule-v2/shiftV2Actions';
import { isPublished } from '@/lib/schedule-v2/shiftV2Status';
import store from '@/store';
import { ActiveTabStyleEnum, PositionEnum, TPageTab } from '@/types/propTypes';
import { ShiftFormTabEnum, ShiftFormV2Type } from '@/types/shiftFormV2';
import {
  addDays,
  minutesBetweenDates,
  startOfDay,
  startOfNextDay,
} from '@/util/dateArithmetic';
import { patchRequestVuelidateForm } from '@/util/endpointFunctions';
import { getEntity, getEntityName } from '@/util/entityFunctions';
import { viewport } from '@/util/windowFunctions';
import { computed, ref } from 'vue';
import {
  Absence,
  Employee,
  LeaveRequest,
  LeaveRequestStatusEnum,
  OpenShiftResponse,
  OpenShiftResponseStatusEnum,
  Shift,
  TagStatusEnum,
  Unavailability,
} from '../../../../api/v1';
import InlineDialog from '../../../components/info/InlineDialog.vue';
import ShiftActionWarnings from './components/ShiftActionWarnings.vue';
import ShiftDialogHeader from './components/ShiftDialogHeader.vue';
import ShiftFormV2 from './components/ShiftFormV2.vue';


export default /*#__PURE__*/_defineComponent({
  __name: 'EditShiftV2',
  props: {
    value: { type: Boolean },
    shift: null,
    openShiftResponses: { default: () => [] },
    showLockedShiftWarning: { type: Boolean, default: false }
  },
  emits: ["close", "input", "createShift"],
  setup(__props: any, { emit }: { emit: ({
  (e: 'close'): void;
  (e: 'input', v?: boolean): void;
  (e: 'createShift', v: EmitCreateShiftData): void;
}), expose: any, slots: any, attrs: any }) {

const props = __props as { value: boolean, shift: Shift, openShiftResponses: OpenShiftResponse[], showLockedShiftWarning: boolean }





const loading = ref<boolean>(false);

const currentTab = ref<ShiftFormTabEnum>(ShiftFormTabEnum.ShiftDetails);

const advancedOptionsTotal = ref<number>(0);

const employees = realtimeQuery(Entity.Employee).fetch();

const timezone = computed<string>(() => store.getters.timezone);

const startDate = ref<Date>(startOfDay(props.shift.startsAt, timezone.value));
const endDate = ref<Date>(
  addDays(
    startOfNextDay(props.shift.endsAt, timezone.value),
    1,
    timezone.value,
  ),
);
const startPlainDate = ref<PlainDate>(
  PlainDate.fromDate(startDate.value, timezone.value),
);
const endPlainDate = ref<PlainDate>(
  PlainDate.fromDate(endDate.value, timezone.value),
);

const unavailability = realtimeQuery(Entity.Unavailability)
  .whereDatesTimesOverlap('startsAt', 'endsAt', startDate.value, endDate.value)
  .fetch();

const leave = realtimeQuery(Entity.LeaveRequest)
  .whereDatesOverlap(
    'startDate',
    'endDate',
    startPlainDate.value as PlainDate,
    endPlainDate.value as PlainDate,
  )
  .fetch();

const closedAbsences = realtimeQuery(Entity.Absence)
  .whereDatesOverlap(
    'startedOn',
    'endedOn',
    startPlainDate.value as PlainDate,
    endPlainDate.value as PlainDate,
  )
  .fetch();

const shifts = realtimeQuery(Entity.Shift)
  .whereDatesTimesOverlap('startsAt', 'endsAt', startDate.value, endDate.value)
  .fetch();

const tags = realtimeQuery(Entity.Tag).whereNotDeleted().fetch();

const jobRoles = realtimeQuery(Entity.JobRole).fetch();
const locations = realtimeQuery(Entity.Location).fetch();

const open = useOpen(() => props.value, emit);

const initialising = computed(() => employees.isLoading);

const employee = computed<Employee>(() => {
  if (!props.shift.employeeId) {
    return null;
  }
  return getEntity(props.shift.employeeId, employees.data);
});

const title = computed<string>(() => {
  if (employee.value) {
    return i18n.tc('schedule.dialog.editFor', 1, {
      name: fullName(employee.value),
    });
  }
  if (props.shift.open) {
    return i18n.tc('schedule.dialog.editOpenShift');
  }
  return i18n.tc('schedule.dialog.editUnassigned');
});

const tabs = computed<TPageTab[]>(() => [
  {
    id: 1,
    checked: currentTab.value === ShiftFormTabEnum.ShiftDetails,
    label: i18n.tc('heading.shiftDetails'),
    action: () => {
      currentTab.value = ShiftFormTabEnum.ShiftDetails;
    },
    value: ShiftFormTabEnum.ShiftDetails,
  },
  {
    id: 2,
    checked: currentTab.value === ShiftFormTabEnum.Advanced,
    label: i18n.tc('label.advanced'),
    action: () => {
      currentTab.value = ShiftFormTabEnum.Advanced;
    },
    value: ShiftFormTabEnum.Advanced,
    stat: advancedOptionsTotal.value,
  },
]);

const employeeUnavailability = computed<Unavailability[]>(() => {
  if (!employee.value) {
    return [];
  }
  return unavailability.data.filter(belongsToEmployee(employee.value.id));
});

const employeeLeave = computed<LeaveRequest[]>(() => {
  if (!employee.value) {
    return [];
  }
  return leave.data.filter(
    (l) =>
      l.employeeId === employee.value.id &&
      [
        LeaveRequestStatusEnum.Approved,
        LeaveRequestStatusEnum.PendingCancellation,
      ].includes(l.status),
  );
});

const employeeAbsence = computed<Absence[]>(() => {
  if (!employee.value) {
    return [];
  }
  return closedAbsences.data.filter(belongsToEmployee(employee.value.id));
});

const employeeShifts = computed<Shift[]>(() => {
  if (!employee.value) {
    return [];
  }
  return shifts.data.filter(belongsToEmployee(employee.value.id));
});

const pendingOpenShiftResponses = computed(() => {
  return props.openShiftResponses.filter(
    (r) => r.status === OpenShiftResponseStatusEnum.Pending,
  );
});

const shiftActions = useShiftActions(
  computed(() => ({
    useActions: true,
    shift: props.shift,
    employee: employee.value,
    employeeActions: false,
    excludedActions: ['addShift', ShiftActionControlEnum.EditShift],
    emit,
  })),
);

const deleteShiftAction = computed<ShiftActionType>(() => {
  return shiftActions.value.find(
    (action) => action.id === ShiftActionControlEnum.DeleteShift,
  );
});

const openShiftHistoryAction = computed<ShiftActionType>(() => {
  return shiftActions.value.find(
    (action) => action.id === ShiftActionControlEnum.ShiftHistory,
  );
});

const viewOpenShiftResponsesAction = computed<ShiftActionType>(() => {
  return shiftActions.value.find(
    (action) => action.id === ShiftActionControlEnum.ViewOpenShiftResponses,
  );
});

const editShift = (
  form: ShiftFormV2Type,
  defaultValues: ShiftFormV2Type,
  publish: boolean = false,
) => {
  loading.value = true;

  // Archived tags don't show on the shift form, so make sure
  // they are still saved along with the shift
  const archivedAssignedTags = tags.data.filter(
    (t) =>
      t.status === TagStatusEnum.Archived && props.shift.tagIds.includes(t.id),
  );
  form.tags = Array.from(
    new Set([...form.tags, ...archivedAssignedTags.map((t) => t.id)]),
  );

  const patchRequestData = patchRequestVuelidateForm(form, defaultValues);
  const lockedDataKeys = [
    'endsAt',
    'notes',
    'tags',
    'scheduledBreaks',
    'shiftAreaSessions',
  ];
  let updateShiftData = Object.keys(patchRequestData).reduce((data, key) => {
    if (props.shift.lockedAt && !lockedDataKeys.includes(key)) {
      return data;
    }
    return {
      ...data,
      [key]: patchRequestData[key],
    };
  }, {});

  if (publish) {
    updateShiftData = {
      ...updateShiftData,
      published: true,
    };
  }

  shiftApi
    .updateShift({
      id: props.shift.id,
      updateShiftData,
    })
    .then(({ data: shift }) => {
      realtimeQueryInstantUpdate(
        Entity.Shift,
        WeakEntityRepositoryUpdateKind.UPDATE,
        shift,
      );
      open.value = false;
    })
    .finally(() => {
      loading.value = false;
    });
};

const conflictingCustomShiftMessage = (
  form: ShiftFormV2Type,
  onlyPublishedShifts: boolean = false,
): ConflictingCustomShiftMessage | null => {
  return form.startsAt && form.endsAt && employee.value
    ? getConflictingCustomShiftMessage(
        [{ startsAt: form.startsAt, endsAt: form.endsAt }],
        1,
        employee.value.firstName,
        employeeUnavailability.value,
        employeeLeave.value,
        employeeAbsence.value,
        employeeShifts.value,
        timezone.value,
        props.shift.id,
        onlyPublishedShifts,
      )
    : null;
};

const saveButtonConditions = (
  invalid: boolean,
  untouched: boolean,
  form: ShiftFormV2Type,
): boolean => {
  const baseCases = invalid || untouched || loading.value;

  const publishedShiftConflicts =
    isPublished(props.shift) &&
    employee.value &&
    !!conflictingCustomShiftMessage(form, true);

  return baseCases || publishedShiftConflicts;
};

const calculateAdditionalPaidMinutes = (form: ShiftFormV2Type): number => {
  if (!form.startsAt || !form.endsAt) {
    return 0;
  }
  const formTotalDuration = minutesBetweenDates(
    form.endsAt,
    form.startsAt,
    timezone.value,
  );
  const formUnpaidBreakMinutes = form.scheduledBreaks.reduce(
    (total, shiftBreak) => {
      if (shiftBreak.paid) {
        return total;
      }
      return total + shiftBreak.durationInMinutes;
    },
    0,
  );

  // We only want to return the additional minutes here so subtract the existing shift paid working minutes
  return (
    formTotalDuration -
    formUnpaidBreakMinutes -
    (props.shift.durationInMinutes - props.shift.unpaidBreakInMinutes)
  );
};

return { __sfc: true,props, emit, loading, currentTab, advancedOptionsTotal, employees, timezone, startDate, endDate, startPlainDate, endPlainDate, unavailability, leave, closedAbsences, shifts, tags, jobRoles, locations, open, initialising, employee, title, tabs, employeeUnavailability, employeeLeave, employeeAbsence, employeeShifts, pendingOpenShiftResponses, shiftActions, deleteShiftAction, openShiftHistoryAction, viewOpenShiftResponsesAction, editShift, conflictingCustomShiftMessage, saveButtonConditions, calculateAdditionalPaidMinutes, Button, ButtonMenu, Dialog, DialogFooter, InputGroup, PageTabs, LoadingDot, LoadingOverlay, dateTimeFormats, ShiftActionControlEnum, InlineDialogTheme, Icon, isNightShift, presentShiftTimes, ActiveTabStyleEnum, PositionEnum, getEntityName, viewport, InlineDialog, ShiftActionWarnings, ShiftDialogHeader, ShiftFormV2 }
}

})